<template>
  <div ref="root" :class="[$style['main-search'], {[$style['main-search--show']]: show}]">
    <div v-if="show" :class="$style['main-search__backdrop']" @click="onClose" />
    <ClientOnly>
      <Btn
        aria-label="Close search"
        shape="round"
        icon-only="System/arrow-left-s-line"
        color="link"
        :tabindex="isHidden ? -1 : 0"
        :class="$style['main-search__close']"
        @click="onBack" />
    </ClientOnly>

    <FormSearch
      v-model="searchTerm"
      :class="$style['main-search__search']"
      :loading="loading"
      :is-hidden="isHidden"
      :tabindex="isHidden ? -1 : 0"
      @close="onClose"
      @enter="onEnter"
      @submit="onSubmit" />

    <SearchSuggestions
      v-if="show"
      ref="content"
      :class="$style['main-search__results']"
      :error="errorMessage"
      :pages="pages"
      :categories="categories"
      :tabindex="isHidden ? -1 : 0"
      :products="products"
      @submit="onSubmit"
      @close="onClose" />
  </div>
</template>
<script setup>
import { watchDebounced } from '@vueuse/core'
const route = useRoute()
const router = useRouter()
const { $lang, $api } = useNuxtApp()
const content = ref(null)
const searchTerm = ref(null)
const show = ref(false)
const ignoreShow = ref(false)
const emit = defineEmits(['back'])
const pages = ref({ type: '', title: '', results: [] })
const categories = ref({ type: '', title: '', results: [] })
const products = ref({ type: '', title: '', results: [] })
const loading = ref(false)
const errorMessage = ref(null)
defineProps({
  isHidden: {
    type: Boolean,
    default: false
  }
})

if (route.params.searchTerm) {
  searchTerm.value = route.params.searchTerm
}

watchDebounced(searchTerm, async () => {
  if (!searchTerm.value) { return }

  try {
    const data = await $api('search', {
      params: { params: searchTerm.value, limits: true }
    })
    if (data) {
      errorMessage.value = null
      pages.value.results = data.pages.results
      categories.value.results = data.categories.results
      products.value.results = data.products.results

      pages.value.title = data.pages.title
      categories.value.title = data.categories.title
      products.value.title = data.products.title
    }

    if (!ignoreShow.value) {
      show.value = !!searchTerm.value
    }
    ignoreShow.value = false
    loading.value = false
  } catch (e) {
    errorMessage.value = 'noResults'
  }
}, { debounce: 200 })

watch(() => route.fullPath, () => {
  show.value = false
  if (!route.name?.includes('search-detail')) {
    searchTerm.value = null
  }
  if (route.params.searchTerm) {
    searchTerm.value = route.params.searchTerm
    ignoreShow.value = true // Little hack to not show the suggestions when navigating back and forth to the search page
  }
})

const onBack = () => {
  show.value = false
  emit('back')
}
const onClose = () => {
  show.value = false
  searchTerm.value = ''
}
const onSubmit = () => {
  if (searchTerm.value.length > 2) {
    show.value = false
    router
      .push({
        name: `${$lang}-search-detail-searchTerm`,
        params: {
          searchTerm: searchTerm.value
        }
      })
      .catch(() => {
        show.value = false
      })
    emit('back')
  }
}
const onEnter = event => {
  event.target.blur()
}

</script>
<style lang="scss" module>
.main-search {
  background-color: var(--white);
  border: 1px solid var(--grey-light);
  border-radius: calc(var(--border-radius) * 6);
  display: flex;
  height: calc(var(--spacing ) * 6.25); // @TODO Don't know why this strange value
  position: relative;
  z-index: calc(var(--z-index-toolbar) + 3);
  transition: .3s;

  &--show,
  &:focus-within {
    border-color: var(--black);
  }

  :global(.transparent-toolbar):not(:hover) & {
    background-color: transparent;

    form {
      input {
        background-color: var(--grey-opacity);

        &::placeholder {
          color: var(--white);
        }
      }
    }

    svg {
      color: white;
    }
  }

  @media (hover: none) and (max-width: #{$breakpoint-tablet - 1}) {
    :global(.transparent-toolbar) & {
      background-color: transparent;

      form {
        input {
          background-color: var(--grey-opacity);

          &::placeholder {
            color: var(--white);
          }
        }
      }

      svg {
        color: white;
      }
    }
  }

  @media (pointer: coarse) and (max-width: #{$breakpoint-tablet - 1}) {
    :global(.transparent-toolbar) & {
      background-color: transparent;

      form {
        input {
          background-color: var(--grey-opacity);

          &::placeholder {
            color: var(--white);
          }
        }
      }

      svg {
        color: white;
      }
    }
  }

  &__backdrop {
    background: var(--backdrop);
    position: fixed;
    inset: 0;
  }

  &__search {
    position: absolute;
    z-index: 1;
  }

  &__close {
    display: none;
    z-index: 2;
  }

  &__results {
    border: 1px solid var(--black);
    box-shadow: 0 5px 100px 0 var(--black-20-opacity);
    border-radius: calc(var(--border-radius) * 3);
    padding-top: calc(var(--spacing ) * 6) ;
    position: absolute;
    overflow: hidden;
    top: 0;
    left: -1px;
    right: -1px;
    width: auto;
    overscroll-behavior: none;
  }

  @media (max-width: #{$breakpoint-tablet - 1}) {
    &__close {
      display: flex;
    }

    &__search {
      input {
        padding-left: calc(var(--spacing ) * 6);
      }
    }
  }
}
</style>
